// Reset game function

import { getP5 } from '../core/p5-instance';
import {
    setPlayer, bullets, enemyBullets, enemies, items, particles, lanes,
    setScore,
    setCombo,
    setMaxCombo,
    setPowerLevel,
    setPowerStock,
    setBombStock,
    setHomingStock,
    setCannonStock,
    setHomingActive,
    setHomingTimer,
    setCannonCharging,
    setCannonActive,
    setCannonTimer,
    setCannonDamageTimer,
    setShieldActive,
    setShieldTimer,
    setShieldCharging,
    setWave,
    setEnemiesDefeated,
    setWaveEnemiesDefeated,
    setCurrentStageIndex,
    setStageTransitionTimer,
    setStageNameDisplayTimer,
    setIsStageTransitioning,
    setPrevStageIndex,
    setBgNebulaOffset,
    setDistantGiantY,
    setBgPlanetY,
    setSubspaceWarpOffset,
    setStageParticleAlpha,
    getDefeatedByType,
    setGameOverTimer,
    setCanRestart,
    setResultPhase,
    setResultDisplayScore,
    setResultEnemyIndex,
    setResultRankShown,
    getResultParticles,
    setShipSelectEffect,
    setLaunchEffect,
    getBombExplosions,
    getBarrageMissiles,
    setBombEffectTimer,
    setIsBombActive,
    setBombDamageTimer,
    setBombTickTimer,
    setBoosterAlpha,
    setStageStartTimer,
    setPlayerEntryY,
    setPanelSlideProgress,
    setWaveStartTimer,
    setIsWaveStarting,
    setBossWarning,
    setBossWarningTimer,
    setLastBossWave,
    setBossDefeatPause,
    setBossPaused,
    setLastSpawnLane,
    setSameLaneCount,
    setSpawnTimer,
    getSelectedShip,
    getPlayerStateMachine,
    setRank,
    setLives,
    setBossName,
    setDyingFadeAlpha
} from '../store/index';
import { CONFIG } from '../config/game-config';
import { getDiffConfig } from '../utils/helpers';
import { setLastAnimatedCombo, setComboAnimTimer } from '../store/animation-state';
import { resetEarthRotation } from '../features/background/three-renderer';
import { EntryState } from '../features/player/states/player-states';

/**
 * resetGame() - ゲームをリセット
 * プレイヤー、敵、弾、スコア、ウェーブなどすべてをリセット
 */
export function resetGame(): void {
    const p = getP5();

    // 難易度設定を取得
    const diffCfg = getDiffConfig();
    const selectedShip = getSelectedShip();

    // プレイヤーオブジェクトを初期化
    setPlayer({
        active: true,
        lane: 0,                 // 現在のレーン（0=左, 1=右）
        x: lanes[0],             // 現在のX座標
        targetX: lanes[0],       // 目標X座標（スムーズ移動用）
        y: p.height - 80,        // Y座標（画面下部に固定）
        size: CONFIG.playerSize  // サイズ
    });

    // 全ての配列をクリア
    bullets.length = 0;
    enemyBullets.length = 0;
    enemies.length = 0;
    items.length = 0;
    particles.length = 0;
    const bombExplosionsArr = getBombExplosions();
    bombExplosionsArr.length = 0;
    const barrageMissilesArr = getBarrageMissiles();
    barrageMissilesArr.length = 0;

    // スコアと状態をリセット
    setScore(0);
    setCombo(0);
    setLastAnimatedCombo(0);
    setComboAnimTimer(0);
    setMaxCombo(0);
    setPowerLevel(1);
    setPowerStock(0);

    // 機体と難易度に応じたアビリティ設定
    if (selectedShip === 'bomber') {
        setBombStock(diffCfg.startBombs);
        setHomingStock(0);
        setCannonStock(0);
    } else if (selectedShip === 'hunter') {
        setBombStock(0);
        setHomingStock(diffCfg.startHoming);
        setCannonStock(0);
    } else if (selectedShip === 'lancer') {
        setBombStock(0);
        setHomingStock(0);
        setCannonStock(diffCfg.startCannon);
    }
    setHomingActive(false);
    setHomingTimer(0);
    setCannonCharging(false);
    setCannonActive(false);
    setCannonTimer(0);
    setCannonDamageTimer(0);

    setShieldActive(false);
    setShieldTimer(0);
    setShieldCharging(false);
    setWave(1);
    setEnemiesDefeated(0);
    setWaveEnemiesDefeated(0);

    // ステージ背景をリセット
    setCurrentStageIndex(0);
    setStageTransitionTimer(0);
    setStageNameDisplayTimer(0);
    setIsStageTransitioning(false);
    setPrevStageIndex(0);
    setBgNebulaOffset(0);
    setDistantGiantY(-50);
    setBgPlanetY(760);
    setSubspaceWarpOffset(0);
    setStageParticleAlpha(1);

    // 敵タイプ別撃破数リセット
    const defeatedByType = getDefeatedByType();
    defeatedByType.normal = 0;
    defeatedByType.tank = 0;
    defeatedByType.fast = 0;
    defeatedByType.armored = 0;
    defeatedByType.speedy = 0;
    defeatedByType.laser = 0;
    defeatedByType.mirror = 0;
    defeatedByType.boss = 0;

    // ゲームオーバー関連リセット
    setGameOverTimer(0);
    setCanRestart(false);

    // リザルト画面リセット
    setResultPhase(0);
    setResultDisplayScore(0);
    setResultEnemyIndex(0);
    setResultRankShown(false);
    const resultParticlesArr = getResultParticles();
    resultParticlesArr.length = 0;

    // 機体選択エフェクトリセット
    setShipSelectEffect(null);
    setLaunchEffect(null);

    // エフェクトのリセット
    setBombEffectTimer(0);
    setIsBombActive(false);
    setBombDamageTimer(0);
    setBombTickTimer(0);
    setBoosterAlpha(255);

    // ステージ開始演出を開始（自機が下から登場）
    setStageStartTimer(CONFIG.stageStart.duration);
    setPlayerEntryY(p.height + 50); // 画面外下から開始
    setPanelSlideProgress(0); // パネルスライドイン開始

    // ウェーブ演出リセット
    setWaveStartTimer(0);
    setIsWaveStarting(false);

    // ボス関連のリセット
    setBossWarning(false);
    setBossWarningTimer(0);
    setLastBossWave(0);
    setBossDefeatPause(0);
    setBossPaused(false);
    setBossName('');

    // ランクと残機のリセット
    setRank('D');
    setLives(3);
    setDyingFadeAlpha(1);

    // レーンバランスのリセット
    setLastSpawnLane(-1);
    setSameLaneCount(0);

    // 敵出現タイマーを負の値にリセット（ステージ開始後の遅延）
    setSpawnTimer(-CONFIG.spawn.initialDelay);

    // 地球の回転をリセット（常に同じ位置から開始）
    resetEarthRotation();

    getPlayerStateMachine().transitionTo(new EntryState());
}
